import { useContext } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { allMainColors, ChartType, PolymorphicChart } from "@foodpilot/foods";
import i18next, { t } from "i18next";

import { formatNumber } from "src/utils/formatting";
import ActionPlanEmptyState from "./ActionPlanEmptyState";
import { ScoreChartValues } from "src/components/ScoreChart/ScoreChart";
import { useGetOneScore } from "src/api/sure-api/score/useGetOneScore";
import { DatavizType } from "src/utils/chart";
import { isAllowedChartType } from "src/utils/chart";
import { isAppInPDFMode } from "src/utils/rendering";
import { RootPropsContext } from "src/components/Puck/config/root";

type ScoreSectionProps = {
    score: number;
    completionPercentage?: number;
    reserveSubtitleSpace?: boolean;
    enabled?: boolean;
    chartData?: ScoreChartValues;
    unit?: string;
    ctaURL?: string;
    scoreId: number;
    isLoading?: boolean;
    maxScore?: number[];
};

export const ScoreSection = (props: ScoreSectionProps) => {
    const {
        score,
        completionPercentage = 0,
        reserveSubtitleSpace = false,
        enabled = true,
        chartData,
        unit,
        ctaURL,
        scoreId,
        isLoading,
        maxScore,
    } = props;
    const theme = useTheme();
    const rootProps = useContext(RootPropsContext);
    const isPdfMode = rootProps?.pageType === "PDF" || isAppInPDFMode();
    const footerMinHeight = theme.spacing(6);
    const ctaMinHeight = isPdfMode ? 0 : theme.spacing(5);

    const { score: selectedScore } = useGetOneScore(scoreId);
    const datavizType = (selectedScore?.datavizType as DatavizType | undefined) ?? undefined;
    const chartTypeValue: ChartType = datavizType && isAllowedChartType(datavizType) ? datavizType : "donut";

    const chartColors = chartData?.colors ?? Object.values(allMainColors).slice(0, chartData?.values.length || 0);
    return (
        <Stack
            spacing={theme.spacing(1)}
            alignItems="center"
            p={theme.spacing(4)}
            sx={{
                height:
                    isPdfMode ? "100%" : (
                        {
                            xs: undefined,
                            lg: "100%",
                        }
                    ),
            }}
        >
            <Typography variant="h2" align="center">
                {t("puck.widgets.actionplan.diagnostic.title")}
            </Typography>
            {reserveSubtitleSpace && (
                <Typography
                    variant="body2"
                    fontWeight={theme.typography.fontWeightMedium}
                    fontSize={theme.typography.h3.fontSize}
                    align="center"
                    sx={{ visibility: "hidden" }}
                >
                    2035
                </Typography>
            )}
            {enabled ?
                <>
                    <Box
                        position="relative"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                        sx={{
                            flexGrow: 1,
                            width: "100%",
                            "& > .MuiStack-root": {
                                gap: theme.spacing(1.5),
                            },
                        }}
                    >
                        <PolymorphicChart
                            chartType={chartTypeValue}
                            values={chartData?.values || []}
                            colors={chartColors}
                            createPalette={chartData?.colors ? false : undefined}
                            labels={chartData?.labels || []}
                            displayTotal={formatNumber(score, i18next.language)}
                            valuesUnit={unit}
                            spacing={2}
                            borderWidth={2}
                            hoverOffset={0}
                            isLoading={isLoading}
                            legend={undefined}
                            valuesFormatterFn={(value: number) => formatNumber(value, i18next.language)}
                            maxScore={chartTypeValue === "raw" ? maxScore : undefined}
                            aggregation={{
                                showAggregateToggle: !isPdfMode && (chartData ? chartData?.values.length > 1 : false),
                                aggregateToggleLabel: t(
                                    "components.chart.aggregate_toggle_label",
                                    "Group scores with less than 1%",
                                ),
                                othersCategoryLabel: t("components.chart.others_category_label", "Others"),
                                othersThresholdPercent: 1,
                            }}
                        />
                    </Box>
                    <Stack
                        gap={theme.spacing(1)}
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                        display="flex"
                        sx={{
                            flexWrap: isPdfMode ? "nowrap" : undefined,
                            minHeight: isPdfMode ? footerMinHeight : undefined,
                            minWidth: 0,
                            paddingBottom: theme.spacing(1.5),
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={theme.typography.fontWeightMedium}
                            color="text.secondary"
                            sx={{
                                minWidth: 0,
                                textAlign: "center",
                                whiteSpace: isPdfMode ? "nowrap" : undefined,
                            }}
                        >
                            {t("puck.widgets.actionplan.diagnostic.questionnaireprogress")}
                        </Typography>
                        <Typography
                            variant="body-medium"
                            sx={{
                                backgroundColor: theme.palette.grey[500],
                                padding: theme.spacing(0.25, 1),
                                borderRadius: theme.spacing(0.5),
                                flexShrink: 0,
                                marginLeft: theme.spacing(1),
                                whiteSpace: "nowrap",
                            }}
                        >
                            {formatNumber(completionPercentage / 100, i18next.language, {
                                maximumFractionDigits: 0,
                                style: "percent",
                            })}
                        </Typography>
                    </Stack>
                    <Box sx={{ minHeight: ctaMinHeight, width: "100%" }}>
                        {!isPdfMode && (
                            <Button variant="ternary" fullWidth size="small" href={ctaURL} disabled={!ctaURL}>
                                <Typography variant="body2" fontWeight={theme.typography.fontWeightBold}>
                                    {t("puck.widgets.actionplan.diagnostic.cta.edit")}
                                </Typography>
                            </Button>
                        )}
                    </Box>
                </>
            :   <ActionPlanEmptyState
                    icon="diagnostic"
                    title={t("puck.widgets.actionplan.diagnostic.nodata")}
                    disabled={!ctaURL}
                    hideCta={isPdfMode}
                    cta={{
                        url: ctaURL,
                        label: t("puck.widgets.actionplan.diagnostic.cta.create"),
                    }}
                />
            }
        </Stack>
    );
};

export default ScoreSection;
